import {defineSlotRecipe} from '@chakra-ui/react3'

export const dialogSlotRecipe = defineSlotRecipe({
    slots: [
        'trigger',
        'backdrop',
        'positioner',
        'content',
        'title',
        'description',
        'closeTrigger',
        'header',
        'body',
        'footer'
    ],
    className: 'chakra-dialog',
    base: {
        backdrop: {
            bg: 'blackAlpha.500',
            backdropFilter: 'blur(8px)',
            pos: 'fixed',
            left: 0,
            top: 0,
            w: '100dvw',
            h: '100dvh',
            zIndex: 'var(--z-index)',
            _open: {
                animationName: 'fade-in',
                animationDuration: 'slow'
            },
            _closed: {
                animationName: 'fade-out',
                animationDuration: 'moderate'
            }
        },
        positioner: {
            display: 'flex',
            width: '100dvw',
            height: '100dvh',
            position: 'fixed',
            left: 0,
            top: 0,
            '--dialog-z-index': 'zIndex.modal',
            zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
            justifyContent: 'center',
            overscrollBehaviorY: 'none',
            px: {base: 'spacing-16', s: 0}
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            maxHeight: {base: '100%', s: '80%'},
            outline: 0,
            borderRadius: 'l',
            textStyle: 'sm',
            my: 'var(--dialog-margin, var(--dialog-base-margin))',
            '--dialog-z-index': 'zIndex.modal',
            zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
            bg: 'bg.panel',
            boxShadow: 'lg',
            _open: {
                animationDuration: 'moderate'
            },
            _closed: {
                animationDuration: 'faster'
            }
        },
        header: {
            display: 'flex',
            justifyContent: 'start',
            gap: '2',
            flex: 0,
            px: {base: 'spacing-32', s: 'spacing-80'},
            pt: {base: 'spacing-32', s: 'spacing-80'},
            pb: {base: 'spacing-24', s: 'spacing-40'}
        },
        body: {
            flex: '1',
            pt: 0,
            px: {base: 'spacing-32', s: 'spacing-80'},
            pb: {base: 'spacing-32', s: 'spacing-80'}
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '3',
            px: '6',
            pt: '2',
            pb: '4'
        },
        title: {
            textStyle: 'display.small',
            color: 'text.primary'
        },
        description: {
            color: 'fg.muted'
        },
        closeTrigger: {
            pos: 'absolute',
            top: {base: 'spacing-16', s: 'spacing-24'},
            insetEnd: {base: 'spacing-16', s: 'spacing-24'}
        }
    },
    variants: {
        placement: {
            center: {
                positioner: {
                    alignItems: 'center'
                },
                content: {
                    '--dialog-base-margin': 'auto',
                    mx: 'auto'
                }
            },
            top: {
                positioner: {
                    alignItems: 'flex-start'
                },
                content: {
                    '--dialog-base-margin': 'spacing.16',
                    mx: 'auto'
                }
            },
            bottom: {
                positioner: {
                    alignItems: 'flex-end'
                },
                content: {
                    '--dialog-base-margin': 'spacing.16',
                    mx: 'auto'
                }
            }
        },
        scrollBehavior: {
            inside: {
                positioner: {
                    overflow: 'hidden'
                },
                content: {
                    maxH: {base: '100%', s: '80%'}
                },
                body: {
                    overflow: 'auto'
                }
            },
            outside: {
                positioner: {
                    overflow: 'auto',
                    pointerEvents: 'auto'
                }
            }
        },
        size: {
            xs: {
                content: {
                    maxW: 'sm'
                }
            },
            sm: {
                content: {
                    maxW: 'md'
                }
            },
            md: {
                content: {
                    maxW: 'lg'
                }
            },
            lg: {
                content: {
                    maxW: '2xl'
                }
            },
            xl: {
                content: {
                    maxW: '4xl'
                }
            },
            cover: {
                positioner: {
                    padding: '10'
                },
                content: {
                    width: '100%',
                    height: '100%',
                    '--dialog-margin': '0'
                }
            },
            full: {
                positioner: {
                    px: 0
                },
                content: {
                    maxW: '100dvw',
                    minH: '100dvh',
                    '--dialog-margin': '0',
                    borderRadius: '0'
                },
                body: {
                    pt: 0,
                    px: 0,
                    pb: 0
                }
            }
        },
        motionPreset: {
            scale: {
                content: {
                    _open: {
                        animationName: 'scale-in, fade-in'
                    },
                    _closed: {
                        animationName: 'scale-out, fade-out'
                    }
                }
            },
            'slide-in-bottom': {
                content: {
                    _open: {
                        animationName: 'slide-from-bottom, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-bottom, fade-out'
                    }
                }
            },
            'slide-in-top': {
                content: {
                    _open: {
                        animationName: 'slide-from-top, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-top, fade-out'
                    }
                }
            },
            'slide-in-left': {
                content: {
                    _open: {
                        animationName: 'slide-from-left, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-left, fade-out'
                    }
                }
            },
            'slide-in-right': {
                content: {
                    _open: {
                        animationName: 'slide-from-right, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-right, fade-out'
                    }
                }
            },
            none: {}
        },
        background: {
            default: {
                content: {
                    '--module-surface-bg-color': 'colors.background.surface.on-section-default',
                    '--module-bg-color': 'colors.background.section.default',
                    backgroundColor: 'background.section.default'
                }
            },
            subtle: {
                content: {
                    '--module-surface-bg-color': 'colors.background.surface.on-section-subtle',
                    '--module-bg-color': 'colors.background.section.subtle',
                    backgroundColor: 'background.section.subtle'
                }
            },
            strong: {
                content: {
                    '--module-surface-bg-color': 'colors.background.surface.on-section-strong',
                    '--module-bg-color': 'colors.background.section.strong',
                    backgroundColor: 'background.section.strong'
                }
            },
            inverse: {
                content: {
                    '--module-surface-bg-color': 'colors.background.surface.on-section-inverted',
                    '--module-bg-color': 'colors.background.section.strong',
                    backgroundColor: 'background.section.strong'
                }
            },
            white: {
                content: {
                    '--module-bg-color': 'colors.primary.base.white',
                    backgroundColor: 'var(--module-bg-color)'
                }
            }
        }
    },
    defaultVariants: {
        size: 'md',
        scrollBehavior: 'outside',
        placement: 'top',
        motionPreset: 'scale'
    }
})
